import { useCallback, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';

import { onActivateKey } from './activate';
import { cx } from './classNames';
import { FieldLabel } from './FieldLabel';
import { ListPopup } from './ListPopup';
import { useOutsideDismiss } from './usePopupPosition';

/**
 * Pendant zu `SfComboBox`: freie Texteingabe mit Vorschlagsliste. Im Altclient
 * nur für den Ja/Nein-Filter der Grid-Filterzeile (`['', 'Yes', 'No']`).
 */
export interface ComboBoxProps {
  value: string | null;
  onChange: (value: string) => void;
  items: string[];
  /** Feste Beschriftung über dem Feld (REQ-24 §2). */
  label?: string;
  /** Zusatz in schwächerer Schrift hinter der Beschriftung. */
  hint?: string;
  /** Nur für echte Beispielwerte — nicht als Ersatz für `label` (REQ-24 §3.2). */
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  showClearButton?: boolean;
  id?: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

export function ComboBox(props: ComboBoxProps) {
  const { onChange } = props;
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [draft, setDraft] = useState<string | null>(null);
  const anchorRef = useRef<HTMLElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useOutsideDismiss(open, close, [anchorRef, popupRef]);

  const value = props.value ?? '';
  const text = draft ?? value;
  const needle = text.trim().toLowerCase();
  const suggestions =
    needle === '' ? props.items : props.items.filter((item) => item.toLowerCase().includes(needle));

  const commit = (next: string) => {
    setDraft(null);
    setOpen(false);
    if (next !== value) onChange(next);
  };

  const clear = () => {
    commit('');
  };
  const toggle = () => {
    if (props.disabled !== true) setOpen((current) => !current);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      const length = Math.max(suggestions.length, 1);
      setFocusedIndex((index) => (index + delta + length) % length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      commit(suggestions[focusedIndex] ?? text);
    } else if (event.key === 'Escape') {
      setDraft(null);
      setOpen(false);
    }
  };

  return (
    <>
      <FieldLabel
        id={props.id}
        elementRef={anchorRef}
        className={props.className}
        style={props.style}
        title={props.title}
        groupClass="e-ddl"
        label={props.label}
        hint={props.hint}
        focused={focused}
        disabled={props.disabled}
        onKeyDown={onKeyDown}
      >
        {({ id }) => (
          <>
            <input
              id={id}
              type="text"
              className="e-input"
              role="combobox"
              aria-expanded={open}
              aria-haspopup="listbox"
              value={text}
              placeholder={props.placeholder}
              readOnly={props.readOnly === true}
              disabled={props.disabled === true}
              onChange={(event) => {
                setDraft(event.target.value);
                setOpen(true);
              }}
              onFocus={() => {
                setFocused(true);
              }}
              onBlur={() => {
                setFocused(false);
              }}
            />
            {props.showClearButton === true ? (
              <span
                role="button"
                tabIndex={0}
                aria-label="Eingabe löschen"
                className={cx(
                  'e-clear-icon',
                  'e-icons',
                  'fa-solid fa-xmark',
                  text === '' && 'e-clear-icon-hide',
                )}
                onClick={clear}
                onKeyDown={onActivateKey(clear)}
              />
            ) : null}
            <span
              role="button"
              tabIndex={0}
              aria-label="Liste öffnen"
              className="e-input-group-icon e-ddl-icon e-icons fa-solid fa-caret-down"
              onClick={toggle}
              onKeyDown={onActivateKey(toggle)}
            />
          </>
        )}
      </FieldLabel>
      <ListPopup<string>
        open={open}
        anchorRef={anchorRef}
        popupRef={popupRef}
        items={suggestions}
        getKey={(item, index) => `${item}-${String(index)}`}
        getText={(item) => item}
        isActive={(item) => item === value}
        focusedIndex={focusedIndex}
        onFocusIndex={setFocusedIndex}
        onSelect={commit}
      />
    </>
  );
}

export default ComboBox;
